import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../api/api";

// tiny helper to read role from token payload or a saved key
function getRole() {
  // if you saved it at login:
  const storedRole = localStorage.getItem("sp_role");
  if (storedRole) return storedRole;

  // otherwise decode token payload (best-effort)
  const tok = localStorage.getItem("sp_token");
  if (!tok) return "user";
  try {
    const payload = JSON.parse(atob(tok.split(".")[1] || ""));
    return payload?.role || "user";
  } catch {
    return "user";
  }
}

export const AllOrders = () => {
  const navigate = useNavigate();
  const [active, setActive] = useState("All");
  const filters = ["All", "Pending", "Dispatched", "Cancelled"];
  const [riderPhone, setRiderPhone] = useState(null);
  const [ridersMap, setRidersMap] = useState({});

  const [orders, setOrders] = useState([]);   // always store arrays here
  const [loading, setLoading] = useState(true);
  const role = getRole(); // "user" | "vendor" | "admin" ...
  

  const toPathByStatus = (status) => {
    if (status === "pending") return "/orders/pending";
    if (["processing", "shipped", "delivered"].includes(status)) return "/orders/complete";
    if (status === "cancelled") return "/orders/cancelled";
    return "/orders";
  };

  // Single effect: choose endpoint by role and normalize the shape
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);

        let res;
        if (role === "vendor" || role === "admin") {
          res = await api.orders.vendorMine();
        } else {
          res = await api.orders.mine();
        }

        // Normalize to an array safely
        // - buyer endpoint often returns { status, count, orders: [...] }
        // - vendor endpoint might return [...] directly (or also an object)
        const list = Array.isArray(res)
          ? res
          : Array.isArray(res?.orders)
          ? res.orders
          : [];

        setOrders(list);
      } catch (e) {
        console.warn("Failed to load orders:", e?.message || e);
        setOrders([]); // keep it an array
      } finally {
        setLoading(false);
      }
    })();
  }, [role]);

  // Use a guarded list everywhere so .filter never runs on non-arrays
  const list = Array.isArray(orders) ? orders : [];

  const filtered = useMemo(() => {
    if (active === "All") return list;
    if (active === "Pending")   return list.filter(o => o.status === "pending");
    if (active === "Dispatched") return list.filter(o => ["shipped","delivered"].includes(o.status));
    if (active === "Cancelled")  return list.filter(o => o.status === "cancelled");
    return list;
  }, [list, active]);

  const total = list.length;
  const pend  = list.filter(o => o.status === "pending").length;
  const disp  = list.filter(o => ["shipped","delivered"].includes(o.status)).length;
  const canc  = list.filter(o => o.status === "cancelled").length;


// LOAD ALL RIDERS ONCE & MAP THEM
useEffect(() => {
  const loadRiders = async () => {
    try {
      const res = await api.riders.getRiders();
      if (res.status && Array.isArray(res.data)) {
        // convert list → object for FAST lookup
        const map = {};
        res.data.forEach(r => {
          map[r.userId] = r; // rider.userId matches order.riderId
        });
        setRidersMap(map);
      }
    } catch (err) {
      console.log("Error loading riders:", err);
    }
  };

  loadRiders();
}, []);


  return (
    <div className="min-h-screen grid place-items-center bg-[#fff8e1]">
      <div className="app-container [font-family:'Nunito',Helvetica] text-[#5d4037] pb-40">

        {/* Topbar */}
        <div className="w-full flex items-center mt-4">
          <button onClick={() => navigate(-1)} className="ml-4 w-9 h-9 rounded-full bg-[#ccb357] grid place-items-center" aria-label="Back">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" stroke="#5d4037" strokeWidth="2" viewBox="0 0 24 24"><path d="M15 18l-6-6 6-6"/></svg>
          </button>
          <h1 className="flex-1 text-center font-bold text-[18px]">All Orders</h1>
          <div className="w-9" />
        </div>

        {/* Search (TODO)*/}
        <div className="px-4 mt-4">
          <div className="relative">
            <input
              placeholder="Search Order"
              className="w-full h-[44px] pl-10 pr-4 rounded-full border border-[#5d4037] bg-[#fff8e1] outline-none"
            />
            <svg className="absolute left-3 top-1/2 -translate-y-1/2" xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" stroke="#5d4037" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-3 mt-4">
            <div className="rounded-[12px] bg-[#eadfb7] p-3">
              <p className="text-[13px]"><span className="inline-block w-2 h-2 rounded-full bg-[#666] mr-2 align-middle" />Total Orders</p>
              <p className="mt-1 font-extrabold text-[18px]">{total}</p>
            </div>
            <div className="rounded-[12px] bg-[#eadfb7] p-3">
              <p className="text-[13px]"><span className="inline-block w-2 h-2 rounded-full bg-[#e7a23a] mr-2 align-middle" />Pending Orders</p>
              <p className="mt-1 font-extrabold text-[18px]">{pend}</p>
            </div>
            <div className="rounded-[12px] bg-[#eadfb7] p-3">
              <p className="text-[13px]"><span className="inline-block w-2 h-2 rounded-full bg-[#6bbf59] mr-2 align-middle" />Dispatched Orders</p>
              <p className="mt-1 font-extrabold text-[18px]">{disp}</p>
            </div>
            <div className="rounded-[12px] bg-[#eadfb7] p-3">
              <p className="text-[13px]"><span className="inline-block w-2 h-2 rounded-full bg-[#c2452d] mr-2 align-middle" />Cancelled Orders</p>
              <p className="mt-1 font-extrabold text-[18px]">{canc}</p>
            </div>
          </div>

          {/* Filters */}
          <div className="flex gap-3 mt-4">
            {filters.map((f) => (
              <button
                key={f}
                onClick={() => setActive(f)}
                className={`px-3 py-1 rounded-[12px] text-[13px] ${
                  active === f ? "bg-[#5d4037] text-[#fff8e1]" : "bg-[#eadfb7] text-[#5d4037]"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        {/* Orders list */}
        <div className="px-4 mt-4 space-y-4">
          {loading && <div className="text-sm opacity-80">Loading orders…</div>}
          {!loading && filtered.length === 0 && <div className="text-sm opacity-80">No orders.</div>}
          {!loading && filtered.map((o) => {
            const when = new Date(o.createdAt).toLocaleString();
            const amount = `₦${(o.totalAmount || 0).toLocaleString()}`;
            const status = o.status || "pending";

            // NEW: find assigned rider
            const rider = ridersMap[o.riderId];

            let stripe = "#e7c85a", pillBg="#f7e8a6", pillText="#7d5d00";
            if (status === "cancelled") { stripe="#c2452d"; pillBg="#f5c5c1"; pillText="#a23526"; }
            if (["processing","shipped","delivered"].includes(status)) { stripe="#6bbf59"; pillBg="#cfeccb"; pillText="#2f6b2b"; }

            return (
              <button
  key={o.orderId || o._id}
  className="w-full text-left relative rounded-[12px] bg-[#eadfb7] p-3 overflow-hidden"
>
  {/* Left stripe */}
  <div
    className="absolute left-0 top-0 bottom-0 w-1.5"
    style={{ background: stripe }}
  />

  <div className="flex items-start justify-between pl-3">
    <div>
      <p className="font-bold">Order #{o.orderId || o._id}</p>
      <p className="text-[13px] mt-1">{(o.items || []).length} items</p>
      <p className="text-[13px]">{when}</p>

      {/* Status pill */}
      <span
        className="inline-block mt-2 text-[12px] px-2 py-1 rounded-full"
        style={{ background: pillBg, color: pillText }}
      >
        {status[0].toUpperCase() + status.slice(1)}
      </span>

      {/* ---------------------------------------------------------------- */}
      {/* ORDER DETAILS CARD — buyer, rider, address */}
      {/* ---------------------------------------------------------------- */}

      <div className="mt-3 bg-[#fff8e1] border border-[#ccb357] rounded-md p-3 text-[13px] space-y-1 shadow-sm">

        {/* Buyer Phone */}
        <div className="flex justify-between">
          <span className="font-semibold">Buyer Phone:</span>
          <span>{o.buyerPhone || "—"}</span>
        </div>

        {/* Rider Phone */}
        {/* Rider Phone (FIXED) */}
            <div className="flex justify-between">
              <span className="font-semibold">Rider Phone:</span>
              <span>{rider?.phoneNumber || "Not assigned"}</span>
            </div>

        {/* Delivery Address */}
        <div className="mt-2">
          <span className="font-semibold block">Delivery Address:</span>

          <span className="block opacity-80 leading-snug">
            {o.shippingAddress?.street || ""}<br />
            {o.shippingAddress?.city || ""}, 
            {o.shippingAddress?.state || ""}<br />
            {o.shippingAddress?.zip || ""}<br />
            {o.shippingAddress?.country || ""}
          </span>
        </div>
      </div>

      {/* ---------------------------------------------------------------- */}
      {/* Vendor Dropdown (unchanged) */}
      {/* ---------------------------------------------------------------- */}
      {role === "vendor" && (
        <div className="mt-2">
          <select
            value={status || ""}
            onChange={async (e) => {
              const newStatus = e.target.value;
              if (!newStatus || newStatus === "---") return;
              try {
                setOrders((prev) =>
                  prev.map((ord) =>
                    ord._id === o._id ? { ...ord, status: newStatus } : ord
                  )
                );

                await api.orders.updateStatus(o._id, { to: newStatus });
                e.target.value = "";

                import("../../socket").then(({ default: socket }) => {
                  socket.emit("orderStatusUpdated", {
                    orderId: o._id,
                    status: newStatus,
                    userId: o.userId,
                  });
                });
              } catch (err) {
                console.error("Failed to update order status:", err);
              }
            }}
            className="mt-1 border border-[#5d4037] text-[13px] rounded-md bg-[#fff8e1] px-2 py-1 focus:outline-none"
          >
            <option value="">--- Select Status ---</option>
            <option value="processing">Processing</option>
            <option value="shipped">Shipped</option>
          </select>
        </div>
      )}
    </div>

    <p className="font-bold whitespace-nowrap">{amount}</p>
  </div>
</button>

            );
          })}
        </div>

        {/* Safe-area bar */}
        <div className="fixed bottom-3 md:bottom-6 left-1/2 -translate-x-1/2 w-full max-w-[1440px] xl:max-w-[1600px] px-6 md:px-10 xl:px-16">
          <div className="h-[22px] bg-[#5d4037] flex items-center justify-center">
            <div className="w-[120px] h-[6px] rounded-full bg-white" />
          </div>
        </div>
      </div>
    </div>
  );
};
